import { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { hivesApi } from "../../services/hivesApi";
import { openPrintWindow } from "../../utils/printWindow";
import { Modal } from "../common/Modal";
import { Button } from "../common/Button";
import { WatermarkSelect } from "./WatermarkSelect";
import { formatDateOnly } from "../../services/hiveActions";

const ACTION_LABELS = {
  Inspection: "Inspection",
  HealthCheck: "Health Check", CloseSeason: "Close Season", IssueFound: "Issue Found",
  PulledFrames: "Pulled Frames", ReplacedFrames: "Replaced Frames",
  AddedFrames: "Added Frames", RotatedFrames: "Rotated Frames",
  // On the printed report too, which is the copy an inspector is
  // most likely to be handed (#432).
  ColonyLost: "Colony Lost",
};

/** "Print Hive Report": hive info + action history + QR code, with an
 * optional Draft/Final watermark. */
export function PrintHiveReport({ hiveId, trigger }) {
  const [open, setOpen] = useState(false);
  const [watermark, setWatermark] = useState("None");
  const [busy, setBusy] = useState(false);
  const qrRef = useRef(null);

  async function print() {
    setBusy(true);
    try {
      const full = await hivesApi.getFull(hiveId);
      const { hive, actions = [] } = full;
      const qrDataUrl = qrRef.current?.toDataURL?.() || "";

      const actionRows = actions
        .map(
          (a) => `<tr>
            <td>${new Date(a.action_date).toLocaleDateString()}</td>
            <td>${ACTION_LABELS[a.action_type] || a.action_type}</td>
            <td>${a.details ? escapeHtml(JSON.stringify(a.details)) : ""}</td>
          </tr>`
        )
        .join("");

      const body = `
        <div class="qr-block">
          ${qrDataUrl ? `<img src="${qrDataUrl}" width="120" height="120" alt="QR code" />` : ""}
          <div>
            <h1>Hive ${escapeHtml(hive.hive_number)}</h1>
            <div class="meta">Status: ${escapeHtml(hive.status)} · Type: ${escapeHtml(hive.hive_type)}</div>
            <div class="meta">Installed: ${formatDateOnly(hive.installation_date) || "—"}</div>
            <div class="meta">Last checked: ${hive.last_checked ? new Date(hive.last_checked).toLocaleString() : "Never"}</div>
          </div>
        </div>
        <h2>Action History</h2>
        <table>
          <thead><tr><th>Date</th><th>Action</th><th>Details</th></tr></thead>
          <tbody>${actionRows || `<tr><td colspan="3">No actions logged yet</td></tr>`}</tbody>
        </table>
      `;

      openPrintWindow(`Hive ${hive.hive_number} Report`, body, { watermark });
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <span onClick={() => setOpen(true)}>{trigger}</span>
      {/* Hidden canvas used only to extract a PNG data URL for the print window. */}
      <div style={{ display: "none" }}>
        <QRCodeCanvas ref={qrRef} value={`beekeeper://hive/${hiveId}`} size={120} />
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Print Hive Report"
        footer={<Button onClick={print} disabled={busy}>{busy ? "Preparing…" : "Print"}</Button>}
      >
        <p>Includes hive info, full action history, and a QR code.</p>
        <WatermarkSelect value={watermark} onChange={setWatermark} />
      </Modal>
    </>
  );
}

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
